import { useState } from "react";
import { DiagTreeNode } from "../types/diag";
import { QueryType } from "../types/dialog-types";
import { severityColor } from "../utils/diagSeverityColor";
import { sortRootsByOccurrences } from "../utils/diagSort";
import { formatTigerDateTime } from "../utils/tigerTime";
import { getTigerResource } from "../utils/tigerResource";

interface Props {
  node: DiagTreeNode;
  queryType: QueryType;
  depth?: number;
  // This row's left indent, in pixels. Recursion threads it so the grouped
  // "Determined by"/"Accounts for" headers and their messages can step in by a
  // character count relative to the row above.
  indent?: number;
  forceExpand?: boolean;
  expandedKeys?: Set<string>;
  onToggle?: (key: string) => void;
  // When the query was for the whole site (more than one turbine), each row
  // shows its turbine's short name just after the time — mirrors the C++
  // diagnostics_dialog.cpp "append the turbine short name" block.
  showTurbineName?: boolean;
  // Maps a message's config_name → turbine short display name.
  turbineShortNames?: Map<string, string>;
  // Right-click on a real diagnostic row (one with a config + message id) opens
  // the row context menu. Threaded through the recursion so nested rows get it.
  onRowContextMenu?: (node: DiagTreeNode, x: number, y: number) => void;
  // Left-mousedown on a real diagnostic row starts a tag drag towards a graph
  // window. Threaded through the recursion.
  onRowMouseDown?: (node: DiagTreeNode, e: React.MouseEvent) => void;
  // When set, each message is prefixed with "[message_id] " (C++ my_show_msg_id
  // / TIGER_DIAG_ID). Threaded through the recursion.
  showMsgId?: boolean;
}

// Caret (12px) + gap (8px) before a row's text — the offset from a row's left
// edge to where its message text starts.
const CONTENT_OFFSET_PX = 20;
// Approximate width of one character in the tree font; used for the 4-character
// indentation steps under high-level messages.
const CHAR_PX = 7;

export function nodeExpansionKey(n: DiagTreeNode): string {
  return `${n.config_name}|${n.message_id}|${n.diag_time}|${n.latch_count}`;
}

interface RelateGroup {
  title: string | null;
  items: DiagTreeNode[];
}

// Children of a high-level message are grouped by their relate_type code and
// shown under a title (mirrors diagnostics_display.cpp / diagnostics_dialog.cpp):
//   "DB" → Determined by, "AF" → Accounts for,
//   "CA" → Other faults present in area <relate_area> (one group per area),
//   "LA" → Latched Messages.
//   "OT" → occurrence-tops (untitled, sorted by severity then count).
//   Other relate types (SR/TI/IT/…) carry no title and render directly.
//   Bucket order matches the C++: untitled-no-relate, occurrence-tops,
//   Determined by, Accounts for, other-area, then latched messages last. The
//   occurrence-top and latched buckets are sorted by severity (then occurrence
//   count); the others keep backend order.
export function groupChildrenByRelate(children: DiagTreeNode[]): RelateGroup[] {
  const noRelate: DiagTreeNode[] = [];
  const occurrenceTops: DiagTreeNode[] = [];
  const determinedBy: DiagTreeNode[] = [];
  const accountsFor: DiagTreeNode[] = [];
  const latched: DiagTreeNode[] = [];
  // "Same area" collections are grouped per relate_area, first-seen order.
  const areaOrder: string[] = [];
  const byArea = new Map<string, DiagTreeNode[]>();
  for (const c of children) {
    if (c.relate_type === "DB") determinedBy.push(c);
    else if (c.relate_type === "AF") accountsFor.push(c);
    else if (c.relate_type === "LA") latched.push(c);
    else if (c.relate_type === "OT") occurrenceTops.push(c);
    else if (c.relate_type === "CA") {
      const area = c.relate_area ?? "";
      if (!byArea.has(area)) {
        byArea.set(area, []);
        areaOrder.push(area);
      }
      byArea.get(area)!.push(c);
    } else noRelate.push(c);
  }
  const groups: RelateGroup[] = [];
  if (noRelate.length) groups.push({ title: null, items: noRelate });
  // Occurrence-tops (a Number-of-Occurrences query, grouped per turbine/area)
  // render untitled like the top-level case, ordered by severity then
  // occurrence count to match the non-grouped sort.
  if (occurrenceTops.length)
    groups.push({ title: null, items: sortRootsByOccurrences(occurrenceTops) });
  if (determinedBy.length)
    groups.push({ title: getTigerResource("DIAG_DETERMINED_BY_RES"), items: determinedBy });
  if (accountsFor.length)
    groups.push({ title: getTigerResource("DIAG_ACCOUNTS_FOR_RES"), items: accountsFor });
  for (const area of areaOrder) {
    const title = area
      ? `${getTigerResource("TIGER_DIAG_OTHER_FAULTS_AREA")} ${area}`
      : getTigerResource("TIGER_DIAG_OTHER_FAULTS_AREA");
    groups.push({ title, items: byArea.get(area)! });
  }
  // Latched messages are ordered by severity (then occurrence count), the same
  // as the top-level "Latched Messages" section — matters for grouped-per-area
  // / grouped-per-turbine queries where latched rows arrive as children.
  if (latched.length)
    groups.push({ title: getTigerResource("TIGER_DIAG_LATCH_MSG"), items: sortRootsByOccurrences(latched) });
  return groups;
}

export function DiagTreeNodeView({
  node,
  queryType,
  depth = 0,
  indent = depth * 20,
  forceExpand = false,
  expandedKeys,
  onToggle,
  showTurbineName = false,
  turbineShortNames,
  onRowContextMenu,
  onRowMouseDown,
  showMsgId = false,
}: Props) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const hasChildren = node.children.length > 0;
  const key = nodeExpansionKey(node);
  const controlled = expandedKeys !== undefined;
  const expanded = controlled ? expandedKeys.has(key) : localExpanded;
  const toggle = () => {
    if (controlled) onToggle?.(key);
    else setLocalExpanded((v) => !v);
  };
  const showChildren = hasChildren && (expanded || forceExpand);
  const color = severityColor(node.severity);
  // An occurrence-top (relate_type "OT") shows its occurrence count instead of a
  // time — at any level (e.g. under a turbine/area group in a grouped
  // Number-of-Occurrences query, where occurrence-tops are the second level).
  const isOccurrencesTop = node.relate_type === "OT";
  const hideTime = queryType === "Invalid" && depth >= 1;
  // Latched messages show "[latch_count] message" (no time), per the C++.
  const isLatched = node.relate_type === "LA";
  // Area / title top-level rows ("Reports in area …", "Alarm Reports") are
  // section titles — message text only, no timestamp (fault_type AR / TI).
  const isTitleRow = node.fault_type === "AR" || node.fault_type === "TI";
  // For whole-site queries, prefix the message with the turbine short name
  // (just after the time/count), e.g. "GT1 - ". Empty when not a site query or
  // the config_name has no known short name.
  const turbineLabel = showTurbineName
    ? turbineShortNames?.get(node.config_name) ?? ""
    : "";
  const turbinePrefix = turbineLabel ? `${turbineLabel} - ` : "";
  // "[message_id] " inserted after the turbine prefix and before the message
  // text when Display ID is on (C++ my_show_msg_id block).
  const idPrefix = showMsgId && node.message_id ? `[${node.message_id}] ` : "";

  return (
    <div className="diag-node">
      <div
        className="diag-node-row"
        style={{
          // Explicit per-side padding: a `padding` shorthand here would reset
          // paddingLeft and wipe out the indent.
          paddingLeft: indent,
          // Compact vertical rhythm (1px padding + tightened line-height) so
          // more diagnostic lines fit at once; see the latched/query/orphan
          // header styles which match.
          paddingTop: 1,
          paddingBottom: 1,
          paddingRight: 4,
          lineHeight: 1.3,
          color,
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
          cursor: hasChildren ? "pointer" : "default",
          // Never wrap a diagnostic line; size to content so the tree pane
          // shows a horizontal scrollbar instead.
          whiteSpace: "nowrap",
          width: "max-content",
        }}
        onClick={() => hasChildren && toggle()}
        onMouseDown={
          onRowMouseDown && node.config_name && node.message_id
            ? (e) => onRowMouseDown(node, e)
            : undefined
        }
        onContextMenu={
          onRowContextMenu && node.config_name && node.message_id
            ? (e) => {
                e.preventDefault();
                // Don't let the tree-pane handler also fire (it would replace
                // this row's target with an empty one).
                e.stopPropagation();
                onRowContextMenu(node, e.clientX, e.clientY);
              }
            : undefined
        }
      >
        <span style={{ width: 12, display: "inline-block" }}>
          {hasChildren ? (showChildren ? "▼" : "▶") : ""}
        </span>
        {isTitleRow ? (
          <span>{turbinePrefix}{idPrefix}{node.diag_message}</span>
        ) : isLatched ? (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            [{node.latch_count}] {turbinePrefix}{idPrefix}{node.diag_message}
          </span>
        ) : isOccurrencesTop ? (
          <>
            <span
              style={{
                minWidth: 60,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                paddingRight: 8,
              }}
            >
              {node.latch_count}
            </span>
            <span>{turbinePrefix}{idPrefix}{node.diag_message}</span>
          </>
        ) : hideTime ? (
          <span>{turbinePrefix}{idPrefix}{node.diag_message}</span>
        ) : (
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {formatTigerDateTime(node.diag_time)} {turbinePrefix}{idPrefix}{node.diag_message}
          </span>
        )}
      </div>
      {showChildren && (
        <div className="diag-node-children">
          {groupChildrenByRelate(node.children).map((group, gi) => {
            const titled = group.title !== null;
            // This row's message text starts at `indent + CONTENT_OFFSET_PX`.
            // Titled groups: the header sits 4 chars in from the parent message
            // text, and each message a further 4 chars in (8 from the parent —
            // its own CONTENT_OFFSET cancels, leaving indent + 8 chars). Untitled
            // groups keep the normal one-level step — except the second level of
            // a Number-of-Occurrences query, which is offset 10 chars so its text
            // clears the parent's "[count]" column.
            const headerIndent = indent + CONTENT_OFFSET_PX + 4 * CHAR_PX;
            const childIndent = titled
              ? indent + 8 * CHAR_PX
              : isOccurrencesTop
                ? indent + 10 * CHAR_PX
                : indent + CONTENT_OFFSET_PX;
            return (
              <div key={gi}>
                {titled && (
                  <div
                    style={{
                      paddingLeft: headerIndent,
                      paddingTop: 1,
                      paddingBottom: 1,
                      lineHeight: 1.3,
                      color: "#374151",
                      fontFamily:
                        "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
                      whiteSpace: "nowrap",
                      width: "max-content",
                    }}
                  >
                    {group.title}
                  </div>
                )}
                {group.items.map((child, i) => (
                  <DiagTreeNodeView
                    key={i}
                    node={child}
                    queryType={queryType}
                    depth={depth + 1}
                    indent={childIndent}
                    forceExpand={forceExpand}
                    expandedKeys={expandedKeys}
                    onToggle={onToggle}
                    showTurbineName={showTurbineName}
                    turbineShortNames={turbineShortNames}
                    onRowContextMenu={onRowContextMenu}
                    onRowMouseDown={onRowMouseDown}
                    showMsgId={showMsgId}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
